<template>
  <div class="onboarding-container">
    <div class="onboarding-card">
      <!-- Progress Steps -->
      <div class="progress-steps">
        <div
          v-for="(step, index) in steps"
          :key="index"
          class="step-item"
          :class="{ active: currentStep === index, completed: currentStep > index }"
        >
          <div class="step-circle">
            <i v-if="currentStep > index" class="el-icon-check"></i>
            <span v-else>{{ index + 1 }}</span>
          </div>
          <span class="step-label">{{ step.label }}</span>
        </div>
      </div>

      <!-- Step 1: Set Username -->
      <div v-if="currentStep === 0" class="step-content">
        <div class="step-header">
          <h2>Welcome to LearningFirst!</h2>
          <p>Let's get started by setting up your profile</p>
        </div>

        <div class="form-section">
          <div class="avatar-section">
            <el-avatar
              :size="80"
              :src="userAvatar"
              :style="{
                backgroundColor: '#e0e0e0',
                fontSize: '32px',
                color: '#409EFF'
              }"
            >
              {{ userName ? userName.charAt(0).toUpperCase() : 'U' }}
            </el-avatar>
          </div>

          <el-form :model="form" :rules="rules" ref="usernameForm" label-position="top">
            <el-form-item label="What should we call you?" prop="userName">
              <el-input
                v-model="form.userName"
                placeholder="Enter your name"
                size="large"
                maxlength="50"
                show-word-limit
              >
                <i slot="prefix" class="el-icon-user"></i>
              </el-input>
            </el-form-item>
          </el-form>

          <div class="hint-text">
            <i class="el-icon-info"></i>
            This name will be visible to other users on the platform.
          </div>
        </div>
      </div>

      <!-- Step 2: (Future) Select Interests -->
      <!-- Add more steps here as needed -->

      <!-- Navigation Buttons -->
      <div class="step-actions">
        <el-button
          v-if="currentStep > 0"
          @click="prevStep"
          size="large"
        >
          Back
        </el-button>
        <el-button
          type="primary"
          @click="nextStep"
          :loading="saving"
          size="large"
        >
          {{ isLastStep ? 'Get Started' : 'Continue' }}
        </el-button>
      </div>

      <!-- Skip Option -->
      <div class="skip-option" v-if="!isLastStep">
        <el-button type="text" @click="skipOnboarding">
          Skip for now
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { postRequest } from '@/apis/front'

export default {
  name: 'Onboarding',
  data() {
    return {
      currentStep: 0,
      saving: false,
      form: {
        userName: ''
      },
      rules: {
        userName: [
          { required: true, message: 'Please enter your name', trigger: 'blur' },
          { min: 2, max: 50, message: 'Name should be 2-50 characters', trigger: 'blur' }
        ]
      },
      steps: [
        { label: 'Set Name', key: 'username' }
        // Add more steps here in the future
        // { label: 'Interests', key: 'interests' },
        // { label: 'Preferences', key: 'preferences' }
      ]
    }
  },
  computed: {
    isLastStep() {
      return this.currentStep === this.steps.length - 1
    },
    userName() {
      return this.form.userName || this.$store.state.user_info?.user_name || ''
    },
    userAvatar() {
      return this.$store.state.user_info?.user_avatar_address_small || ''
    }
  },
  mounted() {
    // Pre-fill username if available
    const existingName = this.$store.state.user_info?.user_name
    if (existingName && existingName !== 'Anonymous User') {
      this.form.userName = existingName
    }
  },
  methods: {
    getOnboardingCompletedKey(userId = this.$store.state.user_id) {
      return userId ? `onboardingCompleted:${userId}` : 'onboardingCompleted'
    },

    async nextStep() {
      if (this.currentStep === 0) {
        // Validate username form
        const valid = await this.$refs.usernameForm.validate().catch(() => false)
        if (!valid) return

        // Save username
        this.saving = true
        try {
          const response = await postRequest('updateUserProfile', {
            user_id: this.$store.state.user_id,
            user_nickName: this.form.userName
          })

          if (response.data.code === 0) {
            // Update store
            this.$store.commit('updateUserName', this.form.userName)
          } else {
            this.$message.error(response.data.msg || 'Failed to save name')
            return
          }
        } catch (error) {
          console.error('Error saving username:', error)
          this.$message.error('Failed to save name')
          return
        } finally {
          this.saving = false
        }
      }

      if (this.isLastStep) {
        this.completeOnboarding()
      } else {
        this.currentStep++
      }
    },

    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep--
      }
    },

    skipOnboarding() {
      this.$confirm('You can always update your profile later in Settings.', 'Skip Setup?', {
        confirmButtonText: 'Skip',
        cancelButtonText: 'Continue Setup',
        type: 'info'
      }).then(() => {
        this.completeOnboarding()
      }).catch(() => {})
    },

    completeOnboarding() {
      // Mark onboarding as completed
      localStorage.setItem(this.getOnboardingCompletedKey(), 'true')

      // Check for redirect URL
      const redirectUrl = this.$route.query.redirect || localStorage.getItem('postLoginRedirect')
      localStorage.removeItem('postLoginRedirect')

      if (redirectUrl) {
        this.$router.push(redirectUrl)
      } else {
        const accountRole = this.$store.state.user_info?.account_role
        this.$router.push(accountRole === 'creator' ? '/my-curation' : '/plaza')
      }

      this.$message.success('Welcome to LearningFirst!')
    }
  }
}
</script>

<style scoped>
.onboarding-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
  padding: 20px;
}

.onboarding-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  padding: 40px;
  width: 100%;
  max-width: 500px;
}

.progress-steps {
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
  gap: 40px;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e0e0e0;
  color: #909399;
  font-weight: 600;
  transition: all 0.3s;
}

.step-item.active .step-circle {
  background: #409EFF;
  color: white;
}

.step-item.completed .step-circle {
  background: #67c23a;
  color: white;
}

.step-label {
  font-size: 12px;
  color: #909399;
}

.step-item.active .step-label {
  color: #409EFF;
  font-weight: 500;
}

.step-item.completed .step-label {
  color: #67c23a;
}

.step-content {
  margin-bottom: 30px;
}

.step-header {
  text-align: center;
  margin-bottom: 30px;
}

.step-header h2 {
  margin: 0 0 8px;
  font-size: 24px;
  color: #303133;
}

.step-header p {
  margin: 0;
  color: #909399;
  font-size: 14px;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.avatar-section {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
}

.hint-text {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #909399;
  background: #f5f7fa;
  padding: 12px 16px;
  border-radius: 8px;
}

.hint-text i {
  color: #409EFF;
}

.step-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.step-actions .el-button {
  min-width: 120px;
}

.skip-option {
  text-align: center;
  margin-top: 20px;
}

.skip-option .el-button {
  color: #909399;
}

/* Responsive */
@media (max-width: 480px) {
  .onboarding-card {
    padding: 30px 20px;
  }

  .progress-steps {
    gap: 20px;
  }

  .step-header h2 {
    font-size: 20px;
  }
}
</style>
